import type { ImageAsset, ProjectRow, WorkCategory, WorkItem } from '@/types'

export const workFilters: Array<'All' | WorkCategory> = [
    'All',
    'Brand',
    'Web',
    'Product',
    'Motion',
    'Experimental'
]

/** `date` is the month the project launched, as YYYY-MM. */
export const workItems: WorkItem[] = [
    {
        slug: 'maison-tevre-rebrand',
        title: "Maison Tevre Rebrand",
        category: 'Brand',
        date: '2025-02',
        cover: { src: '/images/lummi/work-maison-1.jpg', width: 1600, height: 1986, alt: "Minimal bottle scene with soft shadows for the Maison Tevre identity" },
        detail: { src: '/images/lummi/work-maison-2.jpg', width: 1600, height: 1986, alt: "Cosmetic display with the new Maison Tevre labels" },
        summary: "A new identity for a Paris fragrance house that had outgrown its gold foil past. I kept one serif letter from the old mark and built a quieter system of type, colour and texture around it.",
        role: "Brand identity, art direction",
        client: "Maison Tevre"
    },
    {
        slug: 'oriel-gallery-online',
        title: "Oriel Gallery Online",
        category: 'Web',
        date: '2024-12',
        cover: { src: '/images/projects/web-design.png', width: 1460, height: 1078, alt: "Laptop showing the Oriel Gallery website with its Contemporary Perspectives exhibition, on a stone desk in soft afternoon light" },
        detail: { src: '/images/projects/oriel-detail.png', width: 1500, height: 988, alt: "The Oriel Gallery site open on a desktop screen in a calm white studio" },
        summary: "A website for a contemporary gallery that wanted the screen to feel like a white room. Exhibitions open as long, slow scrolls with generous space and captions set like wall labels.",
        role: "Web design, front-end build",
        client: "Oriel Gallery"
    },
    {
        slug: 'kora-health-app',
        title: "Kōra Health App",
        category: 'Product',
        date: '2022-11',
        cover: { src: '/images/projects/product.png', width: 1460, height: 1078, alt: "Phone showing the Kōra Health app home screen with a wellness score, resting on warm concrete in low sunlight" },
        detail: { src: '/images/projects/kora-detail.jpg', width: 1500, height: 1000, alt: "Two hands passing a phone with the Kora app glowing in warm pink" },
        summary: "A booking and wellbeing app for a group of London clinics. We cut onboarding from nine screens to three and gave the whole app a calm, readable voice.",
        role: "Product design, design system",
        client: "Kōra"
    },
    {
        slug: 'halden-launch-film',
        title: "Halden Launch Film",
        category: 'Motion',
        date: '2025-07',
        cover: { src: '/images/projects/motion.png', width: 1672, height: 941, alt: "A sweeping dark sculptural curve lit in gold light, a still from the Halden launch film" },
        detail: { src: '/images/lummi/work-halden-2.jpg', width: 1600, height: 1935, alt: "Dynamic black brushstrokes on a pale ground" },
        summary: "A forty second launch film for Halden's new collection. Type, texture and colour move at the pace of the fabric, with every cut timed to a single breath.",
        role: "Motion design, art direction",
        client: "Halden"
    },
    {
        slug: 'rain-over-neon',
        title: "Rain Over Neon",
        category: 'Experimental',
        date: '2021-11',
        cover: { src: '/images/projects/experimental.png', width: 2170, height: 725, alt: "A woman watching a rainy city skyline at dusk through a streaked window, from Rain Over Neon" },
        detail: { src: '/images/lummi/work-rain-2.jpg', width: 1600, height: 2114, alt: "Portrait washed in a cybernetic glow" },
        summary: "An interactive poster where rain falls across neon type and gathers wherever the cursor rests. A small experiment in making a still layout behave like weather.",
        role: "Concept, interaction design",
        client: "Personal project"
    },
    {
        slug: 'fenwick-and-dale-identity',
        title: "Fenwick & Dale Identity",
        category: 'Brand',
        date: '2024-09',
        cover: { src: '/images/design/fenwick-1.png', width: 1500, height: 1371, alt: "Fenwick & Dale stationery set with envelope and cards in bold geometric shapes" },
        detail: { src: '/images/design/fenwick-2.jpg', width: 1500, height: 1500, alt: "Minimal white brand tag hanging from a fine metal wire" },
        summary: "A sharper identity for a law firm that wanted to sound human without losing its weight. A custom wordmark, a two colour palette and templates the team actually enjoys using.",
        role: "Identity, typography",
        client: "Fenwick & Dale"
    },
    {
        slug: 'olive-season-studio-site',
        title: "Olive Season Studio Site",
        category: 'Web',
        date: '2024-06',
        cover: { src: '/images/projects/olive-cover.jpg', width: 1500, height: 1000, alt: "Hands at a laptop showing the soft pastel Olive Season site" },
        detail: { src: '/images/design/olive-2.png', width: 1130, height: 1500, alt: "Phone view of the Olive Season site against soft peach fabric" },
        summary: "A portfolio site for a small floral studio whose work changes with the seasons. Each collection gets its own palette, taken straight from the flowers on the page.",
        role: "Web design, content structure",
        client: "Olive Season"
    },
    {
        slug: 'lumen-row-dashboard',
        title: "Lumen Row Dashboard",
        category: 'Product',
        date: '2024-03',
        cover: { src: '/images/design/lumen-1.png', width: 1500, height: 1043, alt: "Tablet showing the Lumen Row dashboard line chart beside printed reports" },
        detail: { src: '/images/projects/lumen-detail.png', width: 1500, height: 1056, alt: "Lumen Row reports and charts beside a tablet on the desk" },
        summary: "An analytics dashboard for a fashion label tracking its releases in real time. Dense data made readable with a strict grid, quiet colour and one bright accent for what matters.",
        role: "Product design, data visualisation",
        client: "Lumen Row"
    },
    {
        slug: 'streetwear-after-dark-titles',
        title: "Streetwear After Dark Titles",
        category: 'Motion',
        date: '2022-02',
        cover: { src: '/images/design/streetwear-1.png', width: 1000, height: 1500, alt: "Neon light trails streaking through a city street at night" },
        detail: { src: '/images/projects/streetwear-detail.png', width: 1500, height: 841, alt: "Bold black type running across a white wall, from the title sequence" },
        summary: "Opening titles for a short film about London's late night streetwear scene. Kinetic type that flickers like shop signs after closing time.",
        role: "Title design, motion",
        client: "Lumen Row"
    },
    {
        slug: 'two-sides-of-grey',
        title: "Two Sides of Grey",
        category: 'Brand',
        date: '2023-10',
        cover: { src: '/images/design/grey-1.png', width: 1500, height: 1500, alt: "Textured black and white checkerboard mark on pale grey" },
        detail: { src: '/images/design/grey-2.jpg', width: 818, height: 1500, alt: "Geometric campaign posters clipped to a white brick wall" },
        summary: "A campaign identity for a tailoring line built on contrast: black and white, formal and playful. A chessboard grid became the system behind every layout.",
        role: "Campaign identity, art direction",
        client: "Kōra Tailoring"
    },
    {
        slug: 'the-boardroom-argument',
        title: "The Boardroom Argument",
        category: 'Web',
        date: '2023-04',
        cover: { src: '/images/projects/boardroom-cover.jpg', width: 1050, height: 1500, alt: "Laptop ready for the Boardroom Argument microsite on a quiet green desk" },
        detail: { src: '/images/projects/boardroom-detail.png', width: 1500, height: 1500, alt: "Phone showing one screen of the campaign site on a white desk" },
        summary: "A campaign microsite that turns a heated boardroom debate into a scrolling story. Each argument lands on its own screen, with type that grows louder as tempers rise.",
        role: "Campaign site, web design",
        client: "Fenwick & Dale"
    },
    {
        slug: 'vows-a-wedding-brand',
        title: "Vows, A Wedding Brand",
        category: 'Brand',
        date: '2023-01',
        cover: { src: '/images/design/vows-1.jpg', width: 1208, height: 1500, alt: "Vows stationery flat lay with serif type, a wax seal and a soft pink ribbon" },
        detail: { src: '/images/design/vows-2.png', width: 1130, height: 1500, alt: "Green wax seal pressed with a leaf mark on textured white paper" },
        summary: "A brand for a small London wedding planner who wanted elegance without the frills. Soft serif type, a warm off-white palette and stationery that feels made by hand.",
        role: "Identity, stationery",
        client: "Vows"
    },
    {
        slug: 'petals-and-plaster',
        title: "Petals and Plaster",
        category: 'Web',
        date: '2024-07',
        cover: { src: '/images/projects/petals-cover.png', width: 1500, height: 1130, alt: "Woman in white reading the Petals and Plaster feature on a laptop" },
        detail: { src: '/images/projects/petals-detail.png', width: 1500, height: 1000, alt: "Phone with a soft violet and pink screen from the mobile feature" },
        summary: "An online feature for Oriel's beauty pages, built around soft textures and slow fades. Every image gets room to breathe, and the words arrive only when they are needed.",
        role: "Editorial web design",
        client: "Oriel"
    },
    {
        slug: 'after-midnight-a-sound-site',
        title: "After Midnight, A Sound Site",
        category: 'Experimental',
        date: '2022-08',
        cover: { src: '/images/design/midnight-1.png', width: 1500, height: 1000, alt: "Gradient bars rising like a sound wave in blue, green and pink" },
        detail: { src: '/images/design/midnight-2.jpg', width: 1500, height: 1131, alt: "Pearlescent digital clock reading 11:31 against pastel haze" },
        summary: "A night time site that plays a different ambient loop for every hour after midnight. Colour, type and sound shift together as the clock runs on.",
        role: "Concept, sound and interaction",
        client: "Personal project"
    },
    {
        slug: 'stone-sea-travel-app',
        title: "Stone Sea Travel App",
        category: 'Product',
        date: '2023-11',
        cover: { src: '/images/design/stone-1.jpg', width: 1050, height: 1500, alt: "Hand holding a phone with the Stone Sea map open" },
        detail: { src: '/images/projects/stone-detail.jpg', width: 1050, height: 1500, alt: "Finger tapping through a route map on the Stone Sea tablet view" },
        summary: "A travel app for slow coastal trips around the Mediterranean. Routes, tides and local tips folded into one calm map that still works offline.",
        role: "Product design, prototyping",
        client: "Stone Sea"
    },
    {
        slug: 'halo-skincare',
        title: "Halo Skincare",
        category: 'Brand',
        date: '2021-04',
        cover: { src: '/images/design/halo-1.png', width: 1500, height: 1130, alt: "Halo skincare amber bottles and beige boxes on a wooden tray" },
        detail: { src: '/images/design/halo-2.png', width: 1500, height: 1130, alt: "Halo bottles on a white plinth in soft natural shadow" },
        summary: "An identity for a botanical skincare line launched by Maison Tevre. Tiny daisy marks, a soft lace pattern and labels that read like handwritten notes.",
        role: "Brand identity, packaging",
        client: "Halo"
    },
    {
        slug: 'garden-studies',
        title: "Garden Studies",
        category: 'Web',
        date: '2025-07',
        cover: { src: '/images/projects/garden-cover.jpg', width: 1500, height: 844, alt: "Desktop screen showing a colourful site beside a small potted plant" },
        detail: { src: '/images/projects/garden-detail.jpg', width: 1224, height: 1500, alt: "Phone laid on paper with a pen, ready for the mobile Garden Studies pages" },
        summary: "A personal site of small studies on colour and growth, updated through the seasons. Each page is a single experiment, from a type specimen to a scrolling greenhouse.",
        role: "Web design, creative development",
        client: "Personal project"
    }
]

export const getWorkItem = (slug: string): WorkItem | undefined => workItems.find(item => item.slug === slug)

const homeLabels: Array<{ slug: string, category: string, title?: string }> = [
    { slug: 'maison-tevre-rebrand', category: "Brand Identity" },
    { slug: 'oriel-gallery-online', category: "Web Design" },
    { slug: 'kora-health-app', category: "Product" },
    { slug: 'halden-launch-film', category: "Motion" },
    { slug: 'rain-over-neon', category: "Experimental", title: "Rain Over Neon, An Interactive Poster" }
]

/** The five projects listed on the home page, with their longer labels. */
export const selectedWork: ProjectRow[] = homeLabels.map(({ slug, category, title }) => {
    const item = getWorkItem(slug) as WorkItem

    return {
        slug,
        category,
        title: title ?? item.title,
        date: item.date,
        cover: item.cover
    }
})

type CaseStudyScreen = {
    image: ImageAsset
    caption: string
}

export const caseStudy: {
    title: string
    year: string
    intro: string
    credit: string
    slug: string
    screens: CaseStudyScreen[]
} = {
    title: "Case study of the year: Maison Tevre, in five screens",
    year: '2025',
    intro: "Five screens from the new Maison Tevre website, designed and built alongside the rebrand so the identity and the site grew up together.",
    credit: "Designed and built for Maison Tevre, Paris",
    slug: 'maison-tevre-rebrand',
    screens: [
        {
            image: { src: '/images/design/case-1.png', width: 1129, height: 1500, alt: "Ornate Maison Tevre fragrance bottles on marble" },
            caption: "The opening screen: one warm light, one line of type and nothing else asking for attention."
        },
        {
            image: { src: '/images/lummi/work-maison-1.jpg', width: 1600, height: 1986, alt: "Minimal bottle scene with soft shadows from the Maison Tevre collection pages" },
            caption: "Collection pages sit on a strict grid, so the bottles can stay the loudest thing on screen."
        },
        {
            image: { src: '/images/design/case-2.png', width: 1500, height: 1500, alt: "Amber fragrance bottle among lavender and sage on a wooden block" },
            caption: "A colour system drawn from the fragrance notes themselves, from amber all the way to deep red."
        },
        {
            image: { src: '/images/design/case-3.jpg', width: 1125, height: 1500, alt: "Tablet showing the Maison Tevre journal on a beige cushion in soft light" },
            caption: "The journal: long, slow reads with type set like a printed magazine, and nothing in the margins."
        },
        {
            image: { src: '/images/design/case-4.jpg', width: 1125, height: 1500, alt: "Open Maison Tevre brand book on beige fabric" },
            caption: "The closing screen, where the house signs off with a single line in its new serif."
        }
    ]
}
